import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { Logo } from "@/components/Logo";
import { MultiSelectField, SingleSelectField, Chip } from "./FormControls";
import {
  ROLE_META, BRANCHES, LINK_FIELDS, SAVE_KEY, EMPTY_FORM_V2,
  YEARS_TO_NUMBER, PROJECT_TYPES_KEY, PROJECT_TYPES_MAX, HOW_HEARD_KEY, WORK_MODEL_KEY,
} from "./roleBranches";
import { Loader2, CheckCircle2, XCircle, ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";

// NJM Build Application v2 (2026-02) — pixel/interaction-faithful rebuild of the approved
// prototype ("NJM Build Application v2 (standalone).html"), skinned in the app's own NJM
// Build design tokens (bg-canvas/paper/ink/hairline/brand, font-editorial) instead of the
// prototype's phone-mockup/full-yellow-background wrapper — that wrapper was a design-tool
// export artifact, not meant to ship literally. Flow, field order, validation rules, and
// role-branch logic all match the prototype + Application Form Blueprint exactly.
//
// EXTENDS the live /build/apply (BuildApply.jsx, kept wired and default at that route) —
// this is a NEW component at /build/apply/v2, gated by the `application_form_v2_enabled`
// backend setting (checked on mount below). Posts to the SAME `/v1/build/apply` endpoint,
// additively — same one-record contract (Onboarding Status=Applied, Source=portal-application,
// dedupe, ack email, audit event, read-back verify), just with more fields in the body.

const inputCls = "mt-1.5 w-full rounded-lg border border-hairline bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-faint/70 focus:outline-none focus:border-brand transition-colors";
const labelCls = "text-[11px] font-medium uppercase tracking-wider text-faint";
const sectionTitleCls = "font-editorial text-2xl text-ink";

const STEP_ORDER = ["welcome", "basic", "projects", "primary", "secondary", "branch", "links", "review"];

function loadSaved() {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || !parsed.a) return null;
    return parsed;
  } catch { return null; }
}

export default function BuildApplyV2() {
  const [gateChecked, setGateChecked] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [options, setOptions] = useState(null);

  const saved = useMemo(loadSaved, []);
  const [step, setStep] = useState(saved?.step || 0);
  const [form, setForm] = useState(saved?.a || EMPTY_FORM_V2);
  const [showSaved, setShowSaved] = useState(!!saved);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/v1/build/apply/v2/status").then((r) => setEnabled(!!r.data.enabled)).catch(() => setEnabled(false)).finally(() => setGateChecked(true));
    api.get("/v1/build/apply/field-options").then((r) => setOptions(r.data)).catch(() => setOptions({}));
  }, []);

  useEffect(() => {
    if (!result) localStorage.setItem(SAVE_KEY, JSON.stringify({ step, a: form }));
  }, [step, form, result]);

  const branchQuestions = BRANCHES[form.primary_role] || [];
  const steps = branchQuestions.length > 0 ? STEP_ORDER : STEP_ORDER.filter((s) => s !== "branch");
  const stepId = steps[step];
  const stepIndexVisible = steps.filter((s) => s !== "welcome" && s !== "review").indexOf(stepId) + 1;
  const totalVisible = steps.filter((s) => s !== "welcome" && s !== "review").length;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setBranch = (key, value) => setForm((f) => ({ ...f, branch: { ...f.branch, [key]: value } }));

  const toggleProjectType = (opt) => {
    setForm((f) => {
      const has = f.preferred_project_types.includes(opt);
      if (has) return { ...f, preferred_project_types: f.preferred_project_types.filter((p) => p !== opt) };
      if (f.preferred_project_types.length >= PROJECT_TYPES_MAX) return f;
      return { ...f, preferred_project_types: [...f.preferred_project_types, opt] };
    });
  };

  const pickPrimary = (role) => setForm((f) => ({ ...f, primary_role: role, secondary_roles: f.secondary_roles.filter((r) => r !== role), branch: {} }));
  const pickSecondary = (role) => setForm((f) => ({ ...f, secondary_roles: f.secondary_roles[0] === role ? [] : [role] }));

  const validate = () => {
    if (stepId === "basic") {
      if (!form.name.trim() || !form.registered_gmail.trim() || !form.phone.trim() || !form.city.trim()) return "Name, email, phone, and city are required.";
      if (!form.registered_gmail.includes("@")) return "Enter a valid email address.";
      if (!form.experience_level) return "Select your years of experience.";
      if (!form.work_model) return "Select your preferred work model.";
      if (!form.how_heard) return "Let us know how you heard about NJM Build.";
    }
    if (stepId === "projects" && form.preferred_project_types.length < 1) return "Select at least one project type.";
    if (stepId === "primary" && !form.primary_role) return "Choose your primary expertise.";
    if (stepId === "branch") {
      for (const q of branchQuestions) {
        if (q.optional) continue;
        const v = form.branch[q.key];
        const empty = q.kind === "multi" ? !v || v.length === 0 : !v || !String(v).trim();
        if (empty) return `Answer "${q.label}" to continue.`;
      }
    }
    if (stepId === "links") {
      const any = LINK_FIELDS.some(([k]) => form[k]?.trim()) || form.instagram.trim();
      if (!any) return "Share at least one way to see your work: a link, Instagram, or similar.";
    }
    return "";
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        name: form.name, registered_gmail: form.registered_gmail, phone: form.phone, instagram: form.instagram,
        city: form.city, country: form.country, primary_role: form.primary_role, secondary_roles: form.secondary_roles,
        experience_level: form.experience_level, experience_years: YEARS_TO_NUMBER[form.experience_level],
        work_model: form.work_model, how_heard: form.how_heard, referral_contact: form.referral_contact,
        achievements: form.achievements, bio: form.bio, preferred_project_types: form.preferred_project_types,
        work_links: form.work_links, website: form.website, youtube: form.youtube, vimeo: form.vimeo, behance: form.behance,
        ...form.branch,
      };
      Object.keys(payload).forEach((k) => {
        const v = payload[k];
        if (v === "" || v === undefined || v === null || (Array.isArray(v) && v.length === 0)) delete payload[k];
      });
      const r = await api.post("/v1/build/apply", payload);
      setResult(r.data);
      localStorage.removeItem(SAVE_KEY);
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      if (detail?.code === "missing_link") setError("Share at least one way to see your work: a link, Instagram, YouTube, or similar.");
      else if (detail?.code === "invalid_registered_gmail") setError("Enter a valid email address.");
      else if (detail?.code === "invalid_primary_role") setError("Choose a valid Primary Expertise from the list.");
      else setError("Something went wrong submitting your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const startFresh = () => { localStorage.removeItem(SAVE_KEY); setForm(EMPTY_FORM_V2); setStep(0); setShowSaved(false); };

  if (!gateChecked || !options) {
    return <div className="min-h-screen bg-canvas flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  }

  if (!enabled) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center px-4" data-testid="build-apply-v2-disabled">
        <div className="w-full max-w-sm text-center">
          <Logo theme="light" size="lg" iconOnly showBuildLabel />
          <p className="mt-6 text-sm text-ink-soft">This application form isn't live yet. Please use the current application form instead.</p>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center px-4" data-testid="build-apply-v2-success">
        <div className="w-full max-w-md text-center">
          <Logo theme="light" size="lg" iconOnly showBuildLabel />
          <CheckCircle2 className="h-10 w-10 text-brand mx-auto mt-8" />
          <h1 className="font-editorial text-2xl text-ink mt-4">{result.duplicate ? "Already on file" : "Application received"}</h1>
          <p className="mt-2 text-sm text-ink-soft" data-testid="build-apply-v2-success-message">{result.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas" data-testid="build-apply-v2-page">
      <div className="border-b border-hairline bg-paper px-4 sm:px-8 py-6 flex items-center justify-between sticky top-0 z-10">
        <Logo theme="light" size="lg" iconOnly showBuildLabel />
        {stepId !== "welcome" && stepId !== "review" && (
          <div className="text-[11px] text-faint font-medium" data-testid="build-apply-v2-step-counter">Step {stepIndexVisible} of {totalVisible}</div>
        )}
      </div>
      {stepId !== "welcome" && (
        <div className="h-1 bg-hairline/50" data-testid="build-apply-v2-progress">
          <div className="h-full bg-brand transition-all duration-300" style={{ width: `${stepId === "review" ? 100 : (stepIndexVisible / totalVisible) * 100}%` }} />
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-10">
        {showSaved && stepId !== "welcome" && (
          <div className="mb-6 flex items-center justify-between rounded-lg border border-brand/30 bg-brand/10 px-4 py-2.5 text-xs text-ink" data-testid="build-apply-v2-saved-note">
            Resumed from where you left off.
            <button type="button" onClick={startFresh} data-testid="build-apply-v2-start-fresh" className="underline text-ink-soft hover:text-ink">Start fresh</button>
          </div>
        )}

        {stepId === "welcome" && (
          <div className="text-center py-10">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">Talent Call</div>
            <h1 className="font-editorial text-4xl text-ink mt-2">Apply to NJM Build</h1>
            <p className="mt-4 text-sm text-ink-soft max-w-md mx-auto">A few minutes, role-specific questions, and a place to share your work. We review every application.</p>
            <button type="button" data-testid="build-apply-v2-start-btn" onClick={() => setStep(1)}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-ink hover:bg-brand-dim transition-colors">
              Start Application <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {stepId === "basic" && (
          <section className="space-y-6">
            <h2 className={sectionTitleCls}>Basic details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Full Name *</label><input data-testid="build-apply-v2-field-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
              <div><label className={labelCls}>Email *</label><input data-testid="build-apply-v2-field-registered_gmail" type="email" className={inputCls} value={form.registered_gmail} onChange={(e) => set("registered_gmail", e.target.value)} placeholder="you@gmail.com" /></div>
              <div><label className={labelCls}>Phone *</label><input data-testid="build-apply-v2-field-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
              <div><label className={labelCls}>Instagram handle</label><input data-testid="build-apply-v2-field-instagram" className={inputCls} value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@yourhandle" /></div>
              <div><label className={labelCls}>City *</label><input data-testid="build-apply-v2-field-city" className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
              <div><label className={labelCls}>Country</label><input data-testid="build-apply-v2-field-country" className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} /></div>
            </div>
            <SingleSelectField label="Years of experience *" options={options.experience_level || []} value={form.experience_level} onChange={(v) => set("experience_level", v)} testId="build-apply-v2-field-experience_level" />
            <SingleSelectField label="Preferred work-model *" options={options[WORK_MODEL_KEY] || []} value={form.work_model} onChange={(v) => set("work_model", v)} testId="build-apply-v2-field-work_model" />
            <SingleSelectField label="How did you hear about NJM Build? *" options={options[HOW_HEARD_KEY] || []} value={form.how_heard} onChange={(v) => set("how_heard", v)} testId="build-apply-v2-field-how_heard" />
            <div><label className={labelCls}>Referral contact (optional)</label><input data-testid="build-apply-v2-field-referral_contact" className={inputCls} value={form.referral_contact} onChange={(e) => set("referral_contact", e.target.value)} placeholder="Someone at NJM who referred you" /></div>
            <div><label className={labelCls}>Awards / recognition / accomplishments (optional)</label><textarea data-testid="build-apply-v2-field-achievements" rows={2} className={inputCls} value={form.achievements} onChange={(e) => set("achievements", e.target.value)} /></div>
            <div><label className={labelCls}>Why you'd like to join (optional)</label><textarea data-testid="build-apply-v2-field-bio" rows={3} className={inputCls} value={form.bio} onChange={(e) => set("bio", e.target.value)} /></div>
          </section>
        )}

        {stepId === "projects" && (
          <section className="space-y-4">
            <h2 className={sectionTitleCls}>Preferred project types</h2>
            <p className="text-sm text-ink-soft">Select up to {PROJECT_TYPES_MAX}.</p>
            <div className="flex flex-wrap gap-2" data-testid="build-apply-v2-field-preferred_project_types">
              {(options[PROJECT_TYPES_KEY] || []).map((opt) => (
                <Chip key={opt} active={form.preferred_project_types.includes(opt)}
                  disabled={!form.preferred_project_types.includes(opt) && form.preferred_project_types.length >= PROJECT_TYPES_MAX}
                  onClick={() => toggleProjectType(opt)} testId={`build-apply-v2-project-type-${opt}`}>{opt}</Chip>
              ))}
            </div>
          </section>
        )}

        {(stepId === "primary" || stepId === "secondary") && (
          <section className="space-y-4">
            <h2 className={sectionTitleCls}>{stepId === "primary" ? "Primary expertise" : "Secondary expertise"}</h2>
            <p className="text-sm text-ink-soft">{stepId === "primary" ? "Pick the role that best describes you." : "Optional: pick one, or skip."}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`build-apply-v2-${stepId}-grid`}>
              {(options.primary_role || []).filter((v) => stepId === "secondary" ? v !== form.primary_role : true).map((v) => {
                const active = stepId === "primary" ? form.primary_role === v : form.secondary_roles[0] === v;
                const meta = ROLE_META[v] || { tag: v.slice(0, 2).toUpperCase(), description: "" };
                return (
                  <button key={v} type="button" data-testid={`build-apply-v2-${stepId}-role-${v}`}
                    onClick={() => stepId === "primary" ? pickPrimary(v) : pickSecondary(v)}
                    className={`text-left rounded-xl border p-4 transition-all ${active ? "border-brand bg-brand/10" : "border-hairline hover:border-brand/40"}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-ink">{v}</div>
                      <span className="text-[10px] font-mono text-faint">{meta.tag}</span>
                    </div>
                    <div className="text-[11px] text-faint mt-1">{meta.description}</div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {stepId === "branch" && (
          <section className="space-y-6">
            <h2 className={sectionTitleCls}>About your {form.primary_role} work</h2>
            {branchQuestions.map((q) => (
              <div key={q.key}>
                {q.kind === "multi" && (
                  <MultiSelectField label={q.label} options={options[q.optionsKey] || []} value={form.branch[q.key] || []}
                    onChange={(v) => setBranch(q.key, v)} max={q.max} searchable={q.searchable} optional={q.optional}
                    testId={`build-apply-v2-branch-${q.key}`} />
                )}
                {q.kind === "single" && (
                  <SingleSelectField label={q.label} options={options[q.optionsKey] || []} value={form.branch[q.key] || ""}
                    onChange={(v) => setBranch(q.key, v)} testId={`build-apply-v2-branch-${q.key}`} />
                )}
                {q.kind === "text" && (
                  <div>
                    <label className={labelCls}>{q.label}</label>
                    <input data-testid={`build-apply-v2-branch-${q.key}`} className={inputCls} value={form.branch[q.key] || ""} onChange={(e) => setBranch(q.key, e.target.value)} />
                  </div>
                )}
              </div>
            ))}
          </section>
        )}

        {stepId === "links" && (
          <section className="space-y-4">
            <h2 className={sectionTitleCls}>Your work</h2>
            <p className="text-sm text-ink-soft">At least one required: a link, or your Instagram handle above.</p>
            {LINK_FIELDS.map(([key, label]) => (
              <div key={key}>
                <label className={labelCls}>{label}</label>
                {key === "work_links" ? (
                  <textarea data-testid={`build-apply-v2-field-${key}`} rows={2} className={inputCls} value={form[key]} onChange={(e) => set(key, e.target.value)} />
                ) : (
                  <input data-testid={`build-apply-v2-field-${key}`} className={inputCls} value={form[key]} onChange={(e) => set(key, e.target.value)} />
                )}
              </div>
            ))}
          </section>
        )}

        {stepId === "review" && (
          <section className="space-y-5" data-testid="build-apply-v2-review">
            <h2 className={sectionTitleCls}>Review</h2>
            {[
              ["Basic details", 1, [form.name, form.registered_gmail, form.phone, form.city, form.country].filter(Boolean).join(" · ")],
              ["Project types", 2, form.preferred_project_types.join(", ") || "—"],
              ["Primary expertise", 3, form.primary_role || "—"],
              ["Secondary expertise", 4, form.secondary_roles.join(", ") || "None"],
              ["Your work", branchQuestions.length ? 6 : 5, LINK_FIELDS.filter(([k]) => form[k]).map(([k, l]) => l).join(", ") || form.instagram || "—"],
            ].map(([label, jumpIdx, summary]) => (
              <div key={label} className="flex items-center justify-between gap-3 rounded-lg border border-hairline px-4 py-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-faint">{label}</div>
                  <div className="text-sm text-ink mt-0.5">{summary}</div>
                </div>
                <button type="button" onClick={() => setStep(jumpIdx)} data-testid={`build-apply-v2-review-edit-${label.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-xs text-ink-soft hover:text-ink hover:underline shrink-0 inline-flex items-center gap-0.5">Edit <ChevronRight className="h-3 w-3" /></button>
              </div>
            ))}
          </section>
        )}

        {error && <div data-testid="build-apply-v2-error" className="mt-6 text-xs text-red-600 bg-red-50 rounded-md px-3 py-2 flex items-center gap-2"><XCircle className="h-3.5 w-3.5 shrink-0" />{error}</div>}

        {stepId !== "welcome" && (
          <div className="mt-8 flex items-center justify-between">
            <button type="button" onClick={back} data-testid="build-apply-v2-back-btn" className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {stepId === "review" ? (
              <button type="button" onClick={submit} disabled={submitting} data-testid="build-apply-v2-submit-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50 transition-colors">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />} {submitting ? "Submitting…" : "Submit Application"}
              </button>
            ) : (
              <button type="button" onClick={next} data-testid="build-apply-v2-next-btn"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-ink hover:bg-brand-dim transition-colors">
                Next <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
